// The expected wait while convergence starts the daemon and tray (sdk ADR-016).
import { useEffect, useState } from 'react';
import type { LaunchStateName } from '@localagent/sdk';
import { ShogunSpinner } from '@localagent/ui/components/shogun-spinner';
import { STARTUP_QUIPS, startupPhaseLine } from './daemon-starting';

export interface DaemonStartingScreenProps {
  phase: LaunchStateName | null;
  intervalMs?: number;
  slowAfterMs?: number;
}

export function DaemonStartingScreen({
  phase,
  intervalMs = 2400,
  slowAfterMs = 15_000,
}: DaemonStartingScreenProps) {
  const [quip, setQuip] = useState(0);
  const [slow, setSlow] = useState(false);

  useEffect(() => {
    const rotate = setInterval(() => setQuip((n) => (n + 1) % STARTUP_QUIPS.length), intervalMs);
    return () => clearInterval(rotate);
  }, [intervalMs]);

  useEffect(() => {
    const hint = setTimeout(() => setSlow(true), slowAfterMs);
    return () => clearTimeout(hint);
  }, [slowAfterMs]);

  return (
    <div className="flex h-full min-h-full items-center justify-center bg-background">
      <div
        role="status"
        aria-live="polite"
        data-testid="daemon-starting"
        data-launch-phase={phase ?? 'unknown'}
        className="flex max-w-sm flex-col items-center gap-5 text-center"
      >
        <ShogunSpinner size="lg" />
        <div className="text-title-2 font-semibold text-foreground">Waking the shogun…</div>
        <div data-testid="daemon-starting-phase" className="text-body text-muted-foreground">
          {startupPhaseLine(phase)}
        </div>
        <div
          data-testid="daemon-starting-quip"
          className="h-5 text-body italic text-muted-foreground/80 motion-safe:animate-pulse"
        >
          {STARTUP_QUIPS[quip]}
        </div>
        {slow && (
          <div data-testid="daemon-starting-slow" className="text-caption text-muted-foreground">
            The first launch after an update can take up to a minute.
          </div>
        )}
      </div>
    </div>
  );
}
